import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { HeaderNav } from 'interfaces/navigation/Header';
import { FranchiseListSearch } from 'interfaces/content/Ranking';
import { setPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { PageViewDataLayerObject } from 'services/Gtm/entities';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import { getFranchiseYearSearch } from 'api/Ranking/getFranchiseListItems';
import RankingSearchPage from 'pages/RankingSearch/RankingSearchPage';
import setFranchisePageAdConfig from 'services/Ad/config/setFranchisePageAdConfig';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import getMenu from 'api/Header/getMenu';
import { PreviewData } from 'interfaces/PreviewData';
import Error404 from 'components/Error404';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface FranchiseSearchProps {
  franchiseList: FranchiseListSearch;
  years: string[];
  initialYear: string;
}

const AsiaYearSearch: NextPage<FranchiseSearchProps> = ({
  franchiseList,
  years,
  initialYear,
}: FranchiseSearchProps) => {
  const router = useRouter();
  const [initialSearch, setInitialSearch] = useState('');
  const [initialFilters, setInitialFilters] = useState<{ [key: string]: string | boolean }>({});

  useEffect(() => {
    const queryString = new URLSearchParams(router.asPath.split('?')[1] || '');

    setInitialSearch(queryString.get('Name') || '');
    queryString.delete('Name');

    const filters: { [key: string]: string | boolean } = {};
    queryString.forEach((value, key) => {
      if (value === 'true' || value === 'false') {
        filters[key] = value === 'true';
      } else {
        filters[key] = value;
      }
    });
    setInitialFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchiseList]);

  return franchiseList.listType === 'grid' ? (
    <RankingSearchPage
      franchiseList={franchiseList}
      years={years}
      initialFilters={initialFilters}
      initialYear={initialYear}
      initialSearch={initialSearch}
    />
  ) : (
    <Error404
      heading='Oops! Page not found'
      link='homepage'
      msg='Our apologies. It may have expired or there could be a typo.
          Search for what you are looking for or find what you need on our'
    />
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { franchiseSlug, year }: { franchiseSlug: string; year: string } = context.params as {
      franchiseSlug: string;
      year: string;
    };
    const { previewData } = context as { previewData: PreviewData };
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const franchiseList = await getFranchiseYearSearch(franchiseSlug, year, previewData);
    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(franchiseList), fallbackData.dataLayer);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(franchiseList),
      fallbackData.arcSpanPageConfig,
    );
    const pageAdConfig = safe(() => setFranchisePageAdConfig(franchiseList), fallbackData.pageAdConfig);
    const pianoPageConfig = safe(() => setPianoConfig(franchiseList, true), fallbackData.pianoPageConfig);
    const headData = safe(() => setHeadData(franchiseList), fallbackData.headData);

    if (franchiseSlug === 'asia-future') pageAdConfig.topics = ['asia-future'];
    if (franchiseList.listType === 'list') {
      throw new NotFoundError(`Invalidate Grid Ranking search pages`);
    }

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        franchiseList,
        headData,
        headerNav: menu,
        initialYear: year,
        pageAdConfig,
        pianoPageConfig,
        years: franchiseList.years,
      },
      revalidate: 60, // In seconds
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            franchiseSlug: 'fortune500-asia',
            year: '2023',
          },
        },
      ],
    };
  }

  return {
    fallback: 'blocking',
    paths: [],
  };
};

export default AsiaYearSearch;
